import { fetchWrapRPC } from '../services/api';
import { GetDeploymentResponseFixed, ListDeploymentsResponseFixed } from './api';
import {
  ConfigService_DiscoverKubernetesConfigShortcuts,
  ConfigService_DiscoverKubernetesDeploymentMaxReplicas,
  ConfigService_GetMonitoringConfig,
  DiscoverKubernetesConfigShortcutsResponse,
  DiscoverKubernetesDeploymentMaxReplicasRequest,
  DiscoverKubernetesDeploymentMaxReplicasResponse,
  type GetMonitoringConfigResponse,
} from './gen/ai/h2o/mlops/deployer/v2/config_service_pb';
import {
  type BatchGetDeploymentsMetadataRequest,
  type BatchGetDeploymentsMetadataResponse,
  DeploymentMetadataService_BatchGetDeploymentsMetadata,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_metadata_service_pb';
import {
  type DeleteDeploymentMonitoringRequest,
  type DeleteDeploymentMonitoringResponse,
  type DeleteDeploymentRequest,
  type DeleteDeploymentResponse,
  DeploymentService_DeleteDeployment,
  DeploymentService_DeleteDeploymentMonitoring,
  DeploymentService_GetDeployment,
  DeploymentService_ListDeployments,
  DeploymentService_PauseDeployment,
  DeploymentService_ResumeDeployment,
  DeploymentService_RetryDeployment,
  DeploymentService_UpdateDeployment,
  type GetDeploymentRequest,
  type ListDeploymentsRequest,
  PauseDeploymentRequest,
  PauseDeploymentResponse,
  ResumeDeploymentRequest,
  ResumeDeploymentResponse,
  type RetryDeploymentRequest,
  type RetryDeploymentResponse,
  type UpdateDeploymentRequest,
  type UpdateDeploymentResponse,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_service_pb';
import {
  type BatchGetDeploymentStatusesRequest,
  type BatchGetDeploymentStatusesResponse,
  DeploymentStatusService_BatchGetDeploymentStatuses,
} from './gen/ai/h2o/mlops/deployer/v2/deployment_status_service_pb';
import {
  type CreateEndpointRequest,
  type CreateEndpointResponse,
  type DeleteEndpointRequest,
  type DeleteEndpointResponse,
  EndpointService_CreateEndpoint,
  EndpointService_DeleteEndpoint,
  EndpointService_ListEndpoints,
  EndpointService_UpdateEndpoint,
  type ListEndpointsRequest,
  type ListEndpointsResponse,
  type UpdateEndpointRequest,
  type UpdateEndpointResponse,
} from './gen/ai/h2o/mlops/deployer/v2/endpoint_service_pb';
import { RPC } from './gen/runtime';

const PATH_PREFIX = '/deployer';

// Config

export const getMonitoringConfig = (
  mlopsApiUrl: string,
  requestInit?: { signal?: AbortSignal }
): Promise<GetMonitoringConfigResponse> => {
  return fetchWrapRPC(ConfigService_GetMonitoringConfig, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })({}, requestInit);
};

export const discoverKubernetesConfigShortcuts = (
  mlopsApiUrl: string,
  requestInit?: { signal?: AbortSignal }
): Promise<DiscoverKubernetesConfigShortcutsResponse> => {
  return fetchWrapRPC(ConfigService_DiscoverKubernetesConfigShortcuts, {
    basePath: `${mlopsApiUrl}${PATH_PREFIX}`,
  })({}, requestInit);
};

export const discoverKubernetesDeploymentMaxReplicas = (
  mlopsApiUrl: string,
  request: DiscoverKubernetesDeploymentMaxReplicasRequest = {},
  requestInit?: { signal?: AbortSignal }
): Promise<DiscoverKubernetesDeploymentMaxReplicasResponse> => {
  return fetchWrapRPC(ConfigService_DiscoverKubernetesDeploymentMaxReplicas, {
    basePath: `${mlopsApiUrl}${PATH_PREFIX}`,
  })(request, requestInit);
};

// Deployment

export const deleteDeployment = (
  mlopsApiUrl: string,
  request: DeleteDeploymentRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteDeploymentResponse> => {
  return fetchWrapRPC(DeploymentService_DeleteDeployment, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const getDeployment = (
  mlopsApiUrl: string,
  request: GetDeploymentRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<GetDeploymentResponseFixed> => {
  return fetchWrapRPC(DeploymentService_GetDeployment as RPC<GetDeploymentRequest, GetDeploymentResponseFixed>, {
    basePath: `${mlopsApiUrl}${PATH_PREFIX}`,
  })(request, requestInit);
};

/**
 * Fetches a list of deployments from the MLOps API.
 * This RPC is generally a proxy to the mlops-storage service, where it reads most of the data from
 * `deployment.deployer_data` field which is unstructured `bytes` field and is stored as such in the DB. It is not
 * possible to filter or sort by an unstructured database data, so the only fields that can be used for
 * filtering/sorting in this RPC are `name`, `create_time` and `update_time` as only these fields shadow the base
 * mlops-storage Deployment resource.
 * TODO: remove the above notice when the RPC wll be moved to the mlops-deployer service without the proxy to the mlops-storage.
 * @see https://github.com/h2oai/mlops-deployment-server/blob/c72b820fa0afda350cf095617b18b6a3803e17dd/internal/deployer/deployment/service/conv.go#L40-L42
 */
export const listDeployments = (
  mlopsApiUrl: string,
  request: ListDeploymentsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListDeploymentsResponseFixed> => {
  return fetchWrapRPC(DeploymentService_ListDeployments, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  ) as Promise<ListDeploymentsResponseFixed>;
};

export const retryDeployment = (
  mlopsApiUrl: string,
  request: RetryDeploymentRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<RetryDeploymentResponse> => {
  return fetchWrapRPC(DeploymentService_RetryDeployment, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  ) as Promise<RetryDeploymentResponse>;
};

export const updateDeployment = (
  mlopsApiUrl: string,
  request: UpdateDeploymentRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateDeploymentResponse> => {
  return fetchWrapRPC(DeploymentService_UpdateDeployment, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const pauseDeployment = (
  mlopsApiUrl: string,
  request: PauseDeploymentRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<PauseDeploymentResponse> => {
  return fetchWrapRPC(DeploymentService_PauseDeployment, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const resumeDeployment = (
  mlopsApiUrl: string,
  request: ResumeDeploymentRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ResumeDeploymentResponse> => {
  return fetchWrapRPC(DeploymentService_ResumeDeployment, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const deleteDeploymentMonitoring = (
  mlopsApiUrl: string,
  request: DeleteDeploymentMonitoringRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteDeploymentMonitoringResponse> => {
  return fetchWrapRPC(DeploymentService_DeleteDeploymentMonitoring, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

// Deployment Metadata

export const batchGetDeploymentMetadata = (
  // ... rest of the file remains the same
  mlopsApiUrl: string,
  request: BatchGetDeploymentsMetadataRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<BatchGetDeploymentsMetadataResponse> => {
  return fetchWrapRPC(DeploymentMetadataService_BatchGetDeploymentsMetadata, {
    basePath: `${mlopsApiUrl}${PATH_PREFIX}`,
  })(request, requestInit);
};

// Deployment Status

export const batchGetDeploymentStatuses = (
  mlopsApiUrl: string,
  request: BatchGetDeploymentStatusesRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<BatchGetDeploymentStatusesResponse> => {
  return fetchWrapRPC(DeploymentStatusService_BatchGetDeploymentStatuses, {
    basePath: `${mlopsApiUrl}${PATH_PREFIX}`,
  })(request, requestInit);
};

// Endpoints

export const createEndpoint = (
  mlopsApiUrl: string,
  request: CreateEndpointRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<CreateEndpointResponse> => {
  return fetchWrapRPC(EndpointService_CreateEndpoint, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const deleteEndpoint = (
  mlopsApiUrl: string,
  request: DeleteEndpointRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<DeleteEndpointResponse> => {
  return fetchWrapRPC(EndpointService_DeleteEndpoint, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const listEndpoints = (
  mlopsApiUrl: string,
  request: ListEndpointsRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<ListEndpointsResponse> => {
  return fetchWrapRPC(EndpointService_ListEndpoints, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};

export const updateEndpoint = (
  mlopsApiUrl: string,
  request: UpdateEndpointRequest,
  requestInit?: { signal?: AbortSignal }
): Promise<UpdateEndpointResponse> => {
  return fetchWrapRPC(EndpointService_UpdateEndpoint, { basePath: `${mlopsApiUrl}${PATH_PREFIX}` })(
    request,
    requestInit
  );
};
